import { data, isRouteErrorResponse } from "react-router"
import type { APIError, APIErrorRecord } from "source/shared/types"

// The only statuses we hand back to the browser verbatim. Anything not listed here
// stays a 500, so it still reaches Sentry. `CommonErrorBoundary` types its page map
// against `CommonErrorStatus`, so adding a code here without a page is a compile error.
const COMMON_ERROR_STATUSES = {
  "401": "Unauthorized",
  "403": "Forbidden",
  "404": "Not Found",
  "410": "Gone",
} as const

export type CommonErrorStatus = keyof typeof COMMON_ERROR_STATUSES

export class ApiRequestError extends Error implements APIError {
  errors: APIErrorRecord[]
  body?: unknown

  constructor(request: string, body: unknown) {
    const errors = apiErrorRecords(body)
    const [first] = errors
    super(
      `${request} failed${first ? ` (${first.status} ${first.title})` : ""}`,
    )
    this.name = "ApiRequestError"
    this.errors = errors
    if (!isAPIError(body)) this.body = body
  }
}

export function toApiRequestError(request: string, error: unknown): unknown {
  return error instanceof Error ? error : new ApiRequestError(request, error)
}

function apiErrorRecords(body: unknown): APIErrorRecord[] {
  if (isAPIError(body)) return body.errors
  if (isRailsErrorBody(body)) {
    return [{ status: String(body.status), title: body.error, detail: "" }]
  }
  return []
}

function isRailsErrorBody(
  body: unknown,
): body is { status: number | string; error: string } {
  return (
    !!body &&
    typeof body === "object" &&
    "status" in body &&
    (typeof body.status === "number" || typeof body.status === "string") &&
    "error" in body &&
    typeof body.error === "string"
  )
}

export function isAPIError(error: unknown): error is APIError {
  return (
    !!error &&
    typeof error === "object" &&
    "errors" in error &&
    Array.isArray(error.errors)
  )
}

export function apiErrorCode(error: unknown): string | undefined {
  if (isAPIError(error)) return error.errors[0]?.status
  if (error && typeof error === "object") {
    if ("status" in error) {
      return (error as APIErrorRecord).status
    }
  }
}

export function isCommonErrorStatus(
  code: string | undefined,
): code is CommonErrorStatus {
  return !!code && code in COMMON_ERROR_STATUSES
}

function throwStatus(code: CommonErrorStatus): never {
  throw data(null, {
    status: Number(code),
    statusText: COMMON_ERROR_STATUSES[code],
  })
}

export function throwNotFound(): never {
  throwStatus("404")
}

export function isThrownStatus(error: unknown, code: CommonErrorStatus) {
  if (!error || typeof error !== "object" || !("init" in error)) return false

  return (error as { init: ResponseInit | null }).init?.status === Number(code)
}

export function throwWithApiErrorStatus(error: unknown): never {
  if (error instanceof Response || isRouteErrorResponse(error)) throw error

  const code = apiErrorCode(error)
  if (!isCommonErrorStatus(code)) throw error

  throwStatus(code)
}
